import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { NavigateFunction } from 'react-router-dom';
import ModalFrame from '../../primitives/ModalFrame';
import FullScreenModal from '../../primitives/FullScreenModal';
import NavigationConfirmModal from '../../modals/NavigationConfirmModal';
import XQuestVerificationModal from '../../modals/XQuestVerificationModal';
import type { NavigationConfirmDestination } from '../types';


export interface DailyQuestAuxiliaryModalsProps {
  showXVerificationLoadingModal: boolean;
  activeXModalQuestId: number;
  getXUrlForQuest: (questId: number) => string;
  showXVerificationErrorModal: boolean;
  setShowXVerificationErrorModal: Dispatch<SetStateAction<boolean>>;
  showXVerificationSuccessModal: boolean;
  setShowXVerificationSuccessModal: Dispatch<SetStateAction<boolean>>;
  showModerationInfoModal: boolean;
  setShowModerationInfoModal: Dispatch<SetStateAction<boolean>>;
  showVipInfoModal: boolean;
  setShowVipInfoModal: Dispatch<SetStateAction<boolean>>;
  navigationConfirmModal: {
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  };
  setNavigationConfirmModal: Dispatch<SetStateAction<{
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  }>>;
  onClose: () => void;
  navigate: NavigateFunction;
  t: TFunction;
}

export default function DailyQuestAuxiliaryModals({
  showXVerificationLoadingModal,
  activeXModalQuestId,
  getXUrlForQuest,
  showXVerificationErrorModal,
  setShowXVerificationErrorModal,
  showXVerificationSuccessModal,
  setShowXVerificationSuccessModal,
  showModerationInfoModal,
  setShowModerationInfoModal,
  showVipInfoModal,
  setShowVipInfoModal,
  navigationConfirmModal,
  setNavigationConfirmModal,
  onClose,
  navigate,
  t,
}: DailyQuestAuxiliaryModalsProps) {
  return (
    <>
    {/* Модальне вікно з лоадером перевірки підписки на X */}
    <ModalFrame
      isOpen={showXVerificationLoadingModal}
      onClose={onClose}
      variant="dialog"
      className="max-w-[400px] bg-white px-3xl py-4xl text-text-dark"
      showBackButton={true}
      title=""
    >
      <div className="flex flex-col items-center gap-3xl text-center">
        {/* Лоадер - спінер */}
        <div className="h-12 w-12 animate-[spin_1s_linear_infinite] rounded-full border-4 border-daily-quest-auxiliary-modals-border border-t-accent" />

        {/* Текст */}
        <p className="m-0 [font-family:Lato,sans-serif] text-[16px] leading-[24px] font-normal text-text-muted">
          {t("We are checking if you followed our X profile")}
        </p>
        
        {/* X link chip */}
        <a
          href={getXUrlForQuest(activeXModalQuestId)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-sm rounded-[10px] border border-accent-alpha-25 bg-accent-alpha-10 px-lg py-sm no-underline"
        >
          <img src="/ui/daily-quests/x-logo.png" alt="X" className="h-4 w-4 object-contain" />
          <span className="[font-family:Lato,sans-serif] text-[13px] font-bold text-accent">
            {getXUrlForQuest(activeXModalQuestId)}
          </span>
        </a>
      </div>
    </ModalFrame>

    {/* X quest verification modals — dark themed descendant of ModalFrame */}
    <XQuestVerificationModal
      isOpen={showXVerificationErrorModal}
      onClose={() => setShowXVerificationErrorModal(false)}
      variant="error"
      xUrl={getXUrlForQuest(activeXModalQuestId)}
    />
    <XQuestVerificationModal
      isOpen={showXVerificationSuccessModal}
      onClose={() => setShowXVerificationSuccessModal(false)}
      variant="pending"
      xUrl={getXUrlForQuest(activeXModalQuestId)}
    />
    <XQuestVerificationModal
      isOpen={showModerationInfoModal}
      onClose={() => setShowModerationInfoModal(false)}
      variant="info"
      xUrl={getXUrlForQuest(activeXModalQuestId)}
    />
    
    {/* VIP Info Modal (FullScreenModal, shop card style) */}
    <FullScreenModal
      isOpen={showVipInfoModal}
      onClose={() => setShowVipInfoModal(false)}
      title={t("VIP Status")}
      showBackButton
    >
      <div className="mx-auto flex w-full max-w-[420px] flex-col gap-3xl">
        {/* VIP card (copied from ShopScreen layout, item ID 3 style) */}
        <article className="mx-auto box-border flex w-full max-w-[360px] flex-col gap-lg rounded-xl border-2 border-daily-quest-auxiliary-modals-border-2 bg-[linear-gradient(135deg,var(--color-daily-quest-auxiliary-modals-surface)_0%,var(--color-daily-quest-auxiliary-modals-surface-2)_50%,var(--color-daily-quest-auxiliary-modals-surface-3)_100%)] px-2xl py-[13px]">
          {/* Image */}
          <div className="flex justify-center">
            <div className="relative h-[143px] w-[143px] shrink-0">
              <div className="absolute inset-0 h-[143px] w-[143px] rounded-[18px] bg-[image:var(--gradient-item-spotlight)] blur-[11.75px] aspect-square" />
              <img
                src="/ui/item-icons/1.png"
                alt="VIP"
                className="relative m-[15.89px] h-[111.231px] w-[111.231px] rounded-[18px] object-contain"
              />
            </div>
          </div>

          {/* Title & description */}
          <div className="flex flex-col gap-md">
            <h3 className="m-0 [font-family:'Rubik_One',sans-serif] text-[18px] leading-[30px] font-normal text-white">
              {t("VIP (3 days)")}
            </h3>
            <p className="m-0 whitespace-pre-line [font-family:Lato,sans-serif] text-[14px] leading-[18px] font-light tracking-[-0.747px] text-white">
              {t("Unlock premium features and progress faster on the island.") + "\n" + t("Become a VIP and access exclusive bonuses and special features.")}
            </p>
          </div>

          {/* VIP Benefits */}
          <div className="flex flex-col gap-md">
            {[
              { iconImage: '/ui/shell-icon.png', icon: null, title: t("Income increase"), descriptionText: t("You get 20% more pearl income every second"), descriptionValue: null },
              { iconImage: undefined, icon: '\u23F3', title: t("Additional energy"), descriptionText: t("Get +3 hours added to your max energy reserve"), descriptionValue: null },
              { iconImage: undefined, icon: '\uD83C\uDFE0', title: t("Unlimited buildings"), descriptionText: t("Buy unlimited buildings on the map"), descriptionValue: null },
              { iconImage: undefined, icon: '\uD83D\uDC51', title: t("VIP profile badge"), descriptionText: t("VIP badge visible to other players"), descriptionValue: null },
            ].map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col gap-xs rounded-[10px] border border-accent bg-white-alpha-90 p-lg"
              >
                <div className="m-0 flex items-center gap-md [font-family:Lato,sans-serif] text-[16px] leading-[29px] font-bold tracking-[-0.747px] text-text-dark">
                  {benefit.iconImage ? (
                    <img src={benefit.iconImage} alt="" className="h-6 w-6 object-contain" />
                  ) : (
                    benefit.icon
                  )}{' '}
                  {benefit.title}
                </div>
                {benefit.descriptionValue ? (
                  <div className="flex flex-row items-center gap-xs">
                    <div className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[18px] font-light tracking-[-0.747px] text-text-dark">
                      {benefit.descriptionText}
                    </div>
                    <div className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[18px] font-light tracking-[-0.747px] text-text-dark">
                      {benefit.descriptionValue}
                    </div>
                  </div>
                ) : (
                  <div className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[18px] font-light tracking-[-0.747px] text-text-dark">
                    {benefit.descriptionText}
                  </div>
                )}
              </div>
            ))}
          </div>
        </article>
      </div>
    </FullScreenModal>

    {/* Модалка підтвердження навігації */}
    <NavigationConfirmModal
      isOpen={navigationConfirmModal.isOpen}
      destination={navigationConfirmModal.destination}
      onConfirm={() => {
        if (navigationConfirmModal.destination === 'shop') {
          onClose();
          navigate('/shop');
        } else if (navigationConfirmModal.destination === 'hall_of_fame') {
          onClose();
          navigate('/top');
        } else if (navigationConfirmModal.destination === 'city_hall') {
          onClose();
          navigate('/cityhall');
        }
        setNavigationConfirmModal({ isOpen: false, destination: 'shop' });
      }}
      onClose={() => setNavigationConfirmModal({ isOpen: false, destination: 'shop' })}
    />
    </>
  );
}
